import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  _id: string;
  id: string;
  name: string;
  email: string;
  marketplace: string;
  amazonName?: string;
  gems: number;
  credits: number;
  role: 'user' | 'moderator' | 'admin';
  banStatus: string;
  affiliateCode?: string;
  emailNotifications?: {
    newReview?: boolean;
    weeklyDigest?: boolean;
    marketing?: boolean;
  };
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  setAuth: (token: string, user: User) => void;
  setUser: (user: User) => void;
  logout: () => void;
  setHasHydrated: (state: boolean) => void;
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      _hasHydrated: false,

      setAuth: (token, user) =>
        set({
          token,
          // Ensure both id and _id are set
          user: {
            ...user,
            id: user.id || user._id,
            _id: user._id || user.id,
          },
          isAuthenticated: true,
        }),

      setUser: (user) =>
        set({
          // Ensure both id and _id are set
          user: {
            ...user,
            id: user.id || user._id,
            _id: user._id || user.id,
          },
        }),

      logout: () =>
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        }),

      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
    }),
    {
      name: 'gbr-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Check if the stored token is expired
        if (state?.token && isTokenExpired(state.token)) {
          state.logout();
        }
        state?.setHasHydrated(true);
      },
    }
  )
);
